import React, { useCallback, useEffect, useState } from 'react';
import { holdReasonColors } from '../stockManagementTypes';
import {
  listInventoryHolds,
  releaseInventoryHold,
  InventoryHold,
} from '../../../../services/inventoryApi';
import { AsyncBoundary } from '../../../../components/AsyncBoundary';
import { PlaceHoldModal } from './PlaceHoldModal';
import styles from './HoldReserveTab.module.css';

export const HoldReserveTab: React.FC = () => {
  const [holds, setHolds] = useState<InventoryHold[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<number | null>(null);
  const [showPlaceModal, setShowPlaceModal] = useState(false);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const rows = await listInventoryHolds('active');
      setHolds(rows);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load holds');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleRelease = async (hold: InventoryHold) => {
    if (!window.confirm(`Release hold #${hold.id} on ${hold.sku_code} (${hold.quantity} units)?`)) return;
    setBusyId(hold.id);
    try {
      await releaseInventoryHold(hold.id);
      // Active holds list excludes released rows; drop locally.
      setHolds((prev) => prev.filter((h) => h.id !== hold.id));
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Failed to release hold');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div>
      <div className={styles.subHeader}>
        <p className={styles.subtitle}>Inventory monitoring — detect, investigate & correct in real time</p>
        <div className={styles.subActions}>
          <button
            type="button"
            className={styles.fullInvBtn}
            onClick={fetchData}
            disabled={loading}
            title="Refresh holds from the server"
          >
            {loading ? 'Refreshing…' : 'Full Inventory'}
          </button>
          <button
            type="button"
            className={styles.placeBtn}
            onClick={() => setShowPlaceModal(true)}
          >
            <svg viewBox="0 0 24 24" fill="currentColor"><path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z"/></svg>
            Place Hold
          </button>
        </div>
      </div>

      <AsyncBoundary
        loading={loading && holds.length === 0}
        error={error}
        empty={!loading && holds.length === 0}
        emptyMessage="No active inventory holds"
        onRetry={fetchData}
      >
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>HOLD ID</th>
                <th>SKU</th>
                <th>PRODUCT</th>
                <th>LOT</th>
                <th>QTY HELD</th>
                <th>REASON</th>
                <th>PLACED BY</th>
                <th>DATE</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {holds.map((hold) => {
                const reasonColor = holdReasonColors[hold.reason as keyof typeof holdReasonColors] || '#6B7280';
                return (
                  <tr key={hold.id}>
                    <td className={styles.idCell}>#{hold.id}</td>
                    <td className={styles.skuCell}>{hold.sku_code}</td>
                    <td className={styles.bold}>{hold.product_name || hold.sku_code}</td>
                    <td>{hold.lot_number || '—'}</td>
                    <td className={styles.bold}>{hold.quantity}</td>
                    <td>
                      <span className={styles.reasonBadge} style={{ color: reasonColor }}>
                        <span className={styles.reasonDot} style={{ background: reasonColor }} />
                        {hold.reason}
                      </span>
                    </td>
                    <td>{hold.created_by_name || `User #${hold.created_by_user_id}`}</td>
                    <td>{new Date(hold.created_at).toLocaleDateString()}</td>
                    <td>
                      <button
                        type="button"
                        className={`${styles.actionBtn} ${styles.actionBtnGreen}`}
                        onClick={() => handleRelease(hold)}
                        disabled={busyId === hold.id}
                      >
                        {busyId === hold.id ? 'Releasing…' : 'Release'}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </AsyncBoundary>

      {/* Order Reserves section retained as a placeholder — reservations are
          modeled via the inventory_reservations table tied to orders, not the
          new inventory_holds table. Surfacing them is a separate wire-up. */}
      <div className={styles.reserveHeader}>
        <h3 className={styles.reserveTitle}>Order Reserves</h3>
        <span className={styles.reserveCount}>(driven by order line items — see Orders module)</span>
      </div>

      <PlaceHoldModal
        open={showPlaceModal}
        onClose={() => setShowPlaceModal(false)}
        onCreated={fetchData}
      />
    </div>
  );
};
